'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { addDays, isFuture } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { postCommunityFeedItemApi } from '@flipster/apis/v1/community';
import { PutOpenPositionResponse } from '@flipster/apis/v2/trade/types';
import { FormattedMessage } from '@flipster/i18n';
import { useLocalOverlay } from '@flipster/overlay';
import { SubAccountIdContext } from '@flipster/r230522/normal-sub/sub-account';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { getFlipsterConfigsV1Key, setLocalStorage } from '@flipster/utils/localStorage';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import { useAuth } from '../../shared/auth/state';
import { CommunityFeedQueryKey } from '../../shared/community-feed/post-and-edit/bunja';
import { MovableModal } from '../../shared/movable-modal';
import { getSegmentPositionSide } from '../../shared/position/segment';
import { useClosePosition } from '../../shared/trade-actions';
import { TradeModeContext, type TradeMode } from '../../trade-mode/state';
import { ModalErrorBoundary } from '../../utils/modal-error-boundary';
import { CloseDetail } from '../close-detail';
import { ShareThisTradeModal, shareThisTradeBunja } from '../share-this-trade';
import { PositionCloseModalContext, positionCloseModalBunja } from '../state';
import { FullCloseDetail } from './full-close-detail';

interface Props {
  position: OpenPosition | LegacyOpenPosition;
  onMarketCloseComplete?: () => void;
  accountId?: number;
  tradeMode?: TradeMode;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const FullCloseModal = ({
  position: initPosition,
  onMarketCloseComplete,
  accountId,
  tradeMode,
  isOpen,
  onClose,
  onExit,
}: Props) => {
  return (
    <SubAccountIdContext value={accountId}>
      <TradeModeContext value={tradeMode}>
        <PositionCloseModalContext value={initPosition}>
          <MovableModal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
            <MovableModal.Header>
              <MovableModal.Title label={<FormattedMessage id='Assets.r250401.Full_close' />} />
            </MovableModal.Header>
            <ModalErrorBoundary onError={onClose}>
              <FullCloseModalContent
                onMarketCloseComplete={onMarketCloseComplete}
                accountId={accountId}
                onClose={onClose}
              />
            </ModalErrorBoundary>
          </MovableModal.Root>
        </PositionCloseModalContext>
      </TradeModeContext>
    </SubAccountIdContext>
  );
};

const FullCloseModalContent = ({
  onMarketCloseComplete,
  accountId,
  onClose,
}: {
  onMarketCloseComplete?: () => void;
  accountId?: number;
  onClose: () => void;
}) => {
  const t = useTranslations();
  const { user } = useAuth();
  const overlay = useLocalOverlay();
  const queryClient = useQueryClient();
  const transCommon = useTranslations('Common.r250401');
  const transPositionClose = useTranslations('Position_close.r250401');

  const { positionAtom, bboPriceAtom } = useBunja(positionCloseModalBunja);
  const { shareThisTradeCheckedAtom, shareThisTradeNudgeStateAtom } = useBunja(shareThisTradeBunja);

  const { closePosition, isCloseLoading } = useClosePosition({ onMarketCloseComplete, accountId });
  const [isCloseImmediatelyChecked, setIsCloseImmediatelyChecked] = useState(false);

  const [isShareThisTradeChecked, toggleIsShareThisTradeChecked] = useAtom(shareThisTradeCheckedAtom);
  const [shareThisTradeNudgeState, transitShareThisTradeNudgeState] = useAtom(shareThisTradeNudgeStateAtom);

  const position = useAtomValue(positionAtom);
  const bboPrice = useAtomValue(bboPriceAtom);

  const isShareThisTradeAvailable = Num(position.fundsInvested).gte(100) && Num(position.size).abs().gte(1000);
  const isShareThisTradeAvailableAndChecked = isShareThisTradeAvailable && isShareThisTradeChecked;

  function openShareThisTradeModal(closedPosition: PutOpenPositionResponse) {
    if (!isShareThisTradeAvailable) return;
    overlay.open(({ isOpen, close, unmount }) => (
      <ShareThisTradeModal
        isOpen={isOpen}
        onClose={close}
        onConfirm={() => {
          shareClosedPosition(closedPosition);
          transitShareThisTradeNudgeState(['COMPLETE']);
        }}
        onExit={unmount}
      />
    ));
  }
  async function shareClosedPosition(closedPosition: PutOpenPositionResponse) {
    if (!closedPosition.position) return;
    await postCommunityFeedItemApi(closedPosition.position.symbol, {
      positionCardType: 'CLOSED',
      positionId: closedPosition.position.positionId,
    });
    await queryClient.invalidateQueries({ queryKey: [CommunityFeedQueryKey] });
    snackbar.success({
      message: transPositionClose('Position_shared_in_feed_toast'),
    });
  }

  const handlePositionFullClose = async () => {
    segment.track(CommunityEvent.OrderConfirmationDoneClick, {
      share_feed: isShareThisTradeAvailableAndChecked,
      realized_pnl: Number(position.unrealizedPnl),
      realized_pnl_percentage: Number(position.calcUnrealizedPnlPercent),
      symbol: position.symbol,
      trade_type: 'perpetual',
      side: getSegmentPositionSide(position.side),
      close_immediately: isCloseImmediatelyChecked,
      close_type: 'full_close',
      is_airdrop: Boolean(position.airdropType),
      order_type: 'market_order',
    });

    if (isCloseImmediatelyChecked && user) {
      const key = getFlipsterConfigsV1Key(user.userId, 'skipPositionFullCloseModal');
      setLocalStorage(key, true);
    }

    const res = await closePosition({
      p: position,
      newSize: '0',
      orderType: 'market',
      price: bboPrice,
    });

    if (res?.closedPosition) {
      // social: Sharing n feed is currently available only on full close.
      if (isShareThisTradeAvailableAndChecked) {
        // we don't have to nudge who already shared their trade
        transitShareThisTradeNudgeState(['COMPLETE']);
        shareClosedPosition(res.closedPosition);
      } else {
        const [status, maybeTimestamp] = shareThisTradeNudgeState;
        // share this trade nudge modal
        switch (status) {
          case 'UNSEEN': {
            transitShareThisTradeNudgeState(['READY', Date.now()]);
            openShareThisTradeModal(res.closedPosition);
            break;
          }
          case 'READY': {
            if (!isFuture(addDays(maybeTimestamp, 7))) {
              transitShareThisTradeNudgeState(['COMPLETE']);
              openShareThisTradeModal(res.closedPosition);
            }
            break;
          }
        }
      }
    }
    onClose();
  };

  return (
    <>
      <MovableModal.Content>
        <div className={stack({ gap: 8 })}>
          <FullCloseDetail />
          <CloseDetail mode='full' />
        </div>
        <div className={css({ mt: 16 })}>
          {isShareThisTradeAvailable && (
            <Checkbox
              checked={isShareThisTradeAvailableAndChecked}
              onChange={() => {
                segment.track(CommunityEvent.OrderConfirmPageShareToFeedBoxClick, {
                  is_checked: !isShareThisTradeAvailableAndChecked,
                });
                toggleIsShareThisTradeChecked();
              }}
              css={{ alignItems: 'center' }}
            >
              {transPositionClose('Show_trade_in_feed_option')}
            </Checkbox>
          )}
          <Checkbox
            onChange={() => {
              segment.track(CommunityEvent.OrderConfirmPageCloseImmediatelyBoxClick, {
                is_checked: !isCloseImmediatelyChecked,
              });
              setIsCloseImmediatelyChecked((prev) => !prev);
            }}
          >
            {t('Trade.r251111.Don_t_show_this_again_immediately_close_full_position')}
          </Checkbox>
        </div>
      </MovableModal.Content>
      <MovableModal.Footer>
        <MovableModal.Button
          size='large'
          hierarchy='secondary'
          color='neutral'
          onClick={onClose}
          label={transCommon('Cancel')}
          disabled={isCloseLoading}
        />
        <MovableModal.Button
          size='large'
          label={transPositionClose('Close_now')}
          withSpinner
          isLoading={isCloseLoading}
          onClick={handlePositionFullClose}
        />
      </MovableModal.Footer>
    </>
  );
};
